import { useState, useEffect, useMemo, useRef } from "react";
import { format } from "date-fns";
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  ClickAwayListener,
  DataGrid,
  Grid,
  Grow,
  Icon,
  IconButton,
  Loading,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from "../../../components";
import {
  history,
  OrdemServicoService,
  notification,
  PessoaService,
  OrdemServicosStatusPersonalizadosService,
  SessionService,
  AdzapService,
  MensagemPersonalizadaService,
} from "../../../services";
import { formatDate, formatPrice, responseErros } from "../../../utils";
import {
  FormFiltroAvancado,
  ImportarOrcamentoServicoDialog,
} from "./components";
import { PermisoesHelper, StorageHelper, value } from "../../../helpers";
import { Tabs, Tab, LinearProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import palette from "../../../styles/dark-theme/palette";
import { ListPesquisaSatisfacaoOS } from "../add-ordem-servico/components";
import ListStatusCustom from "./components/list-status-custom.component";

const ordemServicoService = new OrdemServicoService();
const ordemServicosStatusPersonalizadosService =
  new OrdemServicosStatusPersonalizadosService();
const pessoaService = new PessoaService();
const adzapService = new AdzapService();
const mensagemPersonalizadaService = new MensagemPersonalizadaService();

const StyledTabs = styled(Tabs)(() => ({
  minHeight: 40,
  backgroundColor: "#f1f5f9",
  borderRadius: 8,
  padding: 4,
  "& .MuiTabs-indicator": {
    display: "none",
  },
}));

const StyledTab = styled(Tab)(() => ({
  textTransform: "none",
  fontSize: 18,
  minHeight: 32,
  borderRadius: 6,
  padding: "6px 12px",
  color: "#64748b",
  transition: "all 0.2s ease",
  "&.Mui-selected": {
    backgroundColor: "#ffffff",
    color: "#0f172a",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
}));

const ListOrdensServico = () => {
  const tabProps = (index) => {
    return {
      id: `full-width-tab-${index}`,
      "aria-controls": `full-width-tabpanel-${index}`,
    };
  };
  const COOLDOWN_TIME = 3000;
  const STORAGE_KEY = "aba-os-context";
  const cooldownRef = useRef(null);
  const cooldownIntervalRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isCooldown, setIsCooldown] = useState(false);
  const storageHelper = new StorageHelper();
  const [abaValue, setAbaValue] = useState(() => {
    const v = storageHelper.getLocal(STORAGE_KEY);
    return v !== null ? Number(v) : 0;
  });
  const [ordensServico, setOrdensServico] = useState([]);
  const [ordensServicoKanban, setOrdensServicoKanban] = useState([]);
  const [ordensServicoFechadasDia, setOrdensServicoFechadasDia] = useState([]);
  // const [listaOrdemServicoPesquisas, setListaOrdemServicoPesquisas] = useState(
  //   [],
  // );
  const [rowCount, setRowCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [colunasKanban, setColunasKanban] = useState([]);
  const [prestadoresList, setPrestadoresList] = useState([]);
  const [listaPessoas, setListaPessoas] = useState([]);
  const [cooldownProgress, setCooldownProgress] = useState(0);
  const [
    openDialogImportarOrcamentoServico,
    setOpenDialogImportarOrcamentoServico,
  ] = useState(false);
  const permissoesHelper = new PermisoesHelper();
  const sessionService = new SessionService();
  const session = sessionService.getEmpresaAndUsuario();
  permissoesHelper.validarPermisao("ordem-servicos-visualizar");
  const buttonSelectOpcoes = [
    {
      descricao: "Importar Orçamento de Serviço",
      action: () => {
        setOpenDialogImportarOrcamentoServico(true);
        setOpen(false);
      },
      permission: permissoesHelper.temPermisao("ordem-servicos-cadastrar"),
    },
  ];

  const colunas = [
    {
      field: "status",
      headerName: "Status",
      sortable: true,
      flex: 70,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const { value, row } = params;

        const isStatusPadrao =
          (value === "ABERTO" ||
            value === "FECHADO" ||
            value === "CANCELADO") &&
          !row.statusPersonalizadoId;

        const colunaKanban = isStatusPadrao
          ? colunasKanban.find((c) => c.status === value)
          : colunasKanban.find((c) => c.id === row.statusPersonalizadoId);

        const tooltipTitle = colunaKanban?.status ?? value;
        const iconColor = colunaKanban?.cor ?? "#ffff";

        return (
          <Tooltip title={tooltipTitle} arrow placement="right">
            <Icon>
              <i
                className="ph-fill ph-circle"
                style={{ color: iconColor, fontSize: 18 }}
              />
            </Icon>
          </Tooltip>
        );
      },
    },
    {
      field: "serieDocumento",
      headerName: "Série",
      align: "center",
      headerAlign: "center",
      width: 70,
      valueGetter: (params) => value.text(params.value),
    },
    {
      field: "numeroDocumento",
      headerName: "Nº Doc.",
      align: "center",
      headerAlign: "center",
      flex: 70,
      filtrar: true,
    },
    {
      field: "clienteNome",
      headerName: "Cliente",
      width: 320,
      sortable: false,
      valueGetter: (params) =>
        params.value ??
        listaPessoas.find((item) => item.id == params.row.clienteId)
          ?.nomeRazaoSocial,
    },
    {
      field: "dataAbertura",
      headerName: "Abertura",
      align: "center",
      headerAlign: "center",
      flex: 80,
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "dataPrevistaFechamento",
      headerName: "Previsão Fechamento",
      align: "center",
      headerAlign: "center",
      flex: 120,
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "dataFechamento",
      headerName: "Fechamento",
      align: "center",
      headerAlign: "center",
      flex: 90,
      valueGetter: (params) => params.value ?
        format(formatDate.received(params.value), "dd/MM/yyyy") : "",
    },
    {
      field: "totalOrdemServico",
      headerName: "Valor",
      align: "right",
      headerAlign: "right",
      flex: 80,
      sortable: false,
      valueGetter: (params) => formatPrice(params.value, false),
    },
    {
      field: "acoes",
      headerName: "Ações",
      sortable: false,
      flex: 100,
      align: "center",
      headerAlign: "center",
      disableClickEventBubbling: true,
      renderCell: (params) => {
        return (
          <div className="d-flex align-items-center">
            {params.row.pesquisas.length > 0 && (
              <ListPesquisaSatisfacaoOS
                listaOrdemServicoPesquisas={params.row.pesquisas}
              />
            )}
            {permissoesHelper.temPermisao("ordem-servicos-visualizar") && (
              <IconButton
                onClick={() => {
                  onClickEditOrdemServico(params.id);
                }}
              >
                <Tooltip title="Editar" arrow placement="right">
                  <i className="ph-fill ph-pencil" style={{ fontSize: 18 }}></i>
                </Tooltip>
              </IconButton>
            )}
            {permissoesHelper.temPermisao("ordem-servicos-cancelar") &&
              params.row.status === "ABERTO" && (
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onClickCancelarOrdemServico(params.id);
                  }}
                >
                  <Tooltip title="Cancelar" arrow placement="right">
                    <i
                      className="ph-fill ph-prohibit"
                      style={{ fontSize: 18 }}
                    ></i>
                  </Tooltip>
                </IconButton>
              )}
            {permissoesHelper.temPermisao("ordem-servicos-excluir") &&
              params.row.status === "ABERTO" && (
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onClickDeleteOrdemServico(params.id);
                  }}
                >
                  <Tooltip title="Excluir" arrow placement="right">
                    <i
                      className="ph-fill ph-trash"
                      style={{ fontSize: 18 }}
                    ></i>
                  </Tooltip>
                </IconButton>
              )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    buscarPrestadoresServico();
    buscarStatusPersonalizados();
  }, []);

  useEffect(() => {
    if (abaValue === 0 && !ordensServico?.length) {
      buscarOrdensServico();
    }
  }, [abaValue]);

  useEffect(() => {
    if (abaValue === 1 && colunasKanban?.length > 0) {
      if (!ordensServicoKanban?.length || !ordensServicoFechadasDia?.length) {
        buscarOrdensKanbanCombinadas();
      }
    }
  }, [abaValue, colunasKanban]);

  const handleChangeAba = (_, newValue) => {
    setAbaValue(newValue);
    storageHelper.setLocal(STORAGE_KEY, String(newValue));
  };

  const buscarPrestadoresServico = () => {
    const filtro = {
      nonPaginated: true,
      tiposPessoaId: 5,
      attributes: ["id", "nomeRazaoSocial"],
    };
    pessoaService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setPrestadoresList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  useEffect(() => {
    const atualizarLista = (setter, data) => {
      setter((prev) => {
        const found = prev.find((o) => String(o.id) === String(data.id));
        if (!found) return prev;
        return prev.map((o) => {
          if (String(o.id) !== String(data.id)) return o;
          const updated = { ...o };
          updated.statusPersonalizadoId = data.statusPersonalizadoId;
          if (data?.dataFechamento) {
            updated.dataFechamento = data.dataFechamento;
          } else if (data?.status === "FECHADO" && !updated.dataFechamento) {
            updated.dataFechamento = new Date().toISOString();
          } else if (data?.status === "ABERTO") {
            updated.dataFechamento = null;
          }
          return updated;
        });
      });
    };

    const handler = (e) => {
      const data = e.detail;
      if (!data || !data.id) return;
      atualizarLista(setOrdensServico, data);
      atualizarLista(setOrdensServicoKanban, data);
      atualizarLista(setOrdensServicoFechadasDia, data);
    };

    window.addEventListener("kanban:update", handler);
    return () => window.removeEventListener("kanban:update", handler);
  }, [setOrdensServico, setOrdensServicoKanban, setOrdensServicoFechadasDia]);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) {
        clearTimeout(cooldownRef.current);
        cooldownRef.current = null;
      }
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
    };
  }, []);

  const buscarOrdensServico = async function (filtro) {
    setLoading(true);
    const result = await ordemServicoService.getAll(filtro);
    if (!result.isAxiosError) {
      setOrdensServico(result.data.rows);
      setRowCount(result.data.count);
      buscarRelacionamentos(result.data.rows);
      // if (result.data.rows) {
      //   const pesquisas = result.data.rows.flatMap(
      //     (row) => row.pesquisas || [],
      //   );
      //   setListaOrdemServicoPesquisas(pesquisas);
      // }
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarOrdensServicoKanban = async function () {
    const statusPersonalizadosIds = colunasKanban
      .map((c) => c.id)
      .filter((id) => id !== null);
    const result = await ordemServicoService.getAllFiltroAvancado({
      nonPaginated: true,
      status: ["ABERTO"],
      ...(statusPersonalizadosIds.length > 0 && {
        statusPersonalizadoId: statusPersonalizadosIds,
      }),
    });

    if (!result.isAxiosError) {
      const rows = result.data.rows || result.data;
      setOrdensServicoKanban(rows);
      return rows;
    } else {
      responseErros(result);
      return [];
    }
  };

  const buscarOrdensServicoFechadasDia = async function () {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    const isoDate = date.toISOString();
    const result = await ordemServicoService.getAllFiltroAvancado({
      nonPaginated: true,
      status: ["FECHADO"],
      porIntervalo: [{ coluna: "dataFechamento", de: isoDate, ate: isoDate }],
    });
    if (!result.isAxiosError) {
      const rows = result.data.rows || result.data;
      setOrdensServicoFechadasDia(rows);
      return rows;
    } else {
      responseErros(result);
      return [];
    }
  };

  const buscarOrdensKanbanCombinadas = async function () {
    setLoading(true);
    try {
      if (!colunasKanban || colunasKanban.length === 0) {
        console.warn("colunasKanban não está disponível");
        return null;
      }

      const [rowsAbertas, rowsFechadas] = await Promise.all([
        buscarOrdensServicoKanban(),
        buscarOrdensServicoFechadasDia(),
      ]);
      buscarRelacionamentos([...rowsAbertas, ...rowsFechadas]);
      return { rowsAbertas, rowsFechadas };
    } catch (err) {
      responseErros(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const buscarStatusPersonalizados = async function () {
    setLoading(true);
    const result = await ordemServicosStatusPersonalizadosService.getAll();
    if (!result.isAxiosError) {
      const colunasCompletas = [
        { id: null, descricao: "ABERTO", cor: "#4FBB53" },
        ...(result.data || []).sort((a, b) => a.ordem - b.ordem),
        { id: null, descricao: "FECHADO", cor: "#004780" },
        { id: null, descricao: "CANCELADO", cor: "#DC3545" },
      ];
      setColunasKanban(
        colunasCompletas.map((col) => {
          return {
            id: col.id,
            status: col.descricao,
            cor: col.cor,
            cards: [],
          };
        }),
      );
    } else responseErros(result);
    setLoading(false);
  };

  const buscarRelacionamentos = (listaDocumento) => {
    const idsPessoas = [
      ...new Set(
        listaDocumento
          .map(({ clienteId }) => (clienteId ? clienteId : 0))
          .filter(
            (clienteId, index, listaDocumento) =>
              listaDocumento.indexOf(clienteId) === index,
          ),
      ),
    ];
    buscarPessoas(idsPessoas);
  };

  const buscarPessoas = (listaIds) => {
    if (listaIds.length) {
      const filtro = {
        id: listaIds,
        nonPaginated: true,
        attributes: ["id", "nomeRazaoSocial"],
        disabledInclude: true,
      };
      pessoaService.getAll(filtro).then((result) => {
        if (!result.isAxiosError) {
          setListaPessoas((prev) => {
            const mapa = new Map(prev.map((p) => [p.id, p]));
            result.data.forEach((p) => mapa.set(p.id, p));
            return Array.from(mapa.values());
          });
        } else {
          responseErros(result);
        }
      });
    }
  };

  const getPrestadorIdsFromOrdem = (ordem) => {
    if (!ordem) return [];
    if (
      Array.isArray(ordem.prestadoresServicoIds) &&
      ordem.prestadoresServicoIds.length
    )
      return Array.from(
        new Set(
          ordem.prestadoresServicoIds.map((i) => Number(i)).filter(Boolean),
        ),
      );
    if (Array.isArray(ordem.servicos) && ordem.servicos.length)
      return Array.from(
        new Set(
          ordem.servicos
            .map((s) => Number(s.prestadorServicoId))
            .filter(Boolean),
        ),
      );
    return [];
  };

  const onClickCadastrarOrdemServico = function () {
    history.push("/faturamento/ordem-servicos/create");
    setOpen(false);
  };

  const onClickEditOrdemServico = function (id) {
    history.push(`/faturamento/ordem-servicos/${id}`);
  };

  const onClickCancelarOrdemServico = async function (id) {
    const retornoAlerta = await notification.confirmacao(
      "Cancelar!",
      "Tem certeza que deseja cancelar essa ordem de serviço?",
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      const result = await ordemServicoService.cancelar(id);
      if (!result.isAxiosError) {
        buscarOrdensServico();
        notification.alteracaoSucesso();
      } else {
        setLoading(false);
        responseErros(result);
      }
    }
  };

  const onClickDeleteOrdemServico = async function (id) {
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      "Tem certeza que deseja excluir essa ordem de serviço?",
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      const result = await ordemServicoService.deletar(id);
      if (!result.isAxiosError) {
        buscarOrdensServico();
        notification.deletadoSucesso();
      } else {
        setLoading(false);
        responseErros(result);
      }
    }
  };

  const buscarPesquisaAvancada = async (filter) => {
    const porIntervalo = [];
    const filtrosAvancadosTemp = {
      ...filter,
    };
    if (
      filtrosAvancadosTemp.dataAberturaStart &&
      filtrosAvancadosTemp.dataAberturaEnd
    ) {
      porIntervalo.push({
        coluna: "dataAbertura",
        de: filtrosAvancadosTemp.dataAberturaStart,
        ate: filtrosAvancadosTemp.dataAberturaEnd,
      });
    }
    if (
      filtrosAvancadosTemp.dataPrevistaFechamentoStart &&
      filtrosAvancadosTemp.dataPrevistaFechamentoEnd
    ) {
      porIntervalo.push({
        coluna: "dataPrevistaFechamento",
        de: filtrosAvancadosTemp.dataPrevistaFechamentoStart,
        ate: filtrosAvancadosTemp.dataPrevistaFechamentoEnd,
      });
    }
    delete filtrosAvancadosTemp.dataAberturaStart;
    delete filtrosAvancadosTemp.dataAberturaEnd;
    delete filtrosAvancadosTemp.dataPrevistaFechamentoStart;
    delete filtrosAvancadosTemp.dataPrevistaFechamentoEnd;

    setLoading(true);
    const result = await ordemServicoService.getAllFiltroAvancado({
      ...filtrosAvancadosTemp,
      restritiva: true,
      porIntervalo,
    });
    if (!result.isAxiosError) {
      setOrdensServico(result.data.rows);
      setRowCount(result.data.count);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const sendServerDatagrid = (props) => {
    if (props.tipoFiltro == "simples") {
      buscarOrdensServico(props.filtros);
    } else {
      buscarPesquisaAvancada(props.filtros);
    }
  };

  const pertenceAColuna = (ordem, coluna) => {
    if (ordem.statusPersonalizadoId) {
      return ordem.statusPersonalizadoId === coluna.id;
    }
    if (coluna.status === "FECHADO") {
      return !ordem.statusPersonalizadoId && ordem.status === "FECHADO";
    }
    if (coluna.status === "ABERTO") {
      return !ordem.statusPersonalizadoId && ordem.status === "ABERTO";
    }
    return false;
  };

  const getNovosValoresStatus = (destCol) => {
    switch (destCol.status) {
      case "ABERTO":
        return {
          statusPersonalizadoId: null,
          dataFechamento: null,
          status: "ABERTO",
        };

      case "FECHADO":
        return {
          statusPersonalizadoId: null,
          dataFechamento: new Date().toISOString(),
          status: "FECHADO",
        };

      default:
        return {
          statusPersonalizadoId: destCol.id,
          dataFechamento: null,
          status: "ABERTO",
        };
    }
  };

  const iniciarCooldown = () => {
    setIsCooldown(true);
    setCooldownProgress(0);
    const start = Date.now();
    if (cooldownRef.current) clearTimeout(cooldownRef.current);
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
      cooldownIntervalRef.current = null;
    }
    cooldownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / COOLDOWN_TIME) * 100);
      setCooldownProgress(pct);
      if (elapsed >= COOLDOWN_TIME) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
    }, 100);
    cooldownRef.current = setTimeout(() => {
      setIsCooldown(false);
      setCooldownProgress(100);
      cooldownRef.current = null;
    }, COOLDOWN_TIME);
  };

  const enviarMensagem = async (ordem, destCol, cliente) => {
    try {
      const getPrimeiroNome = (nomeCompleto) =>
        nomeCompleto?.split(" ")[0] || "";
      const nomeCliente = cliente?.nomeRazaoSocial || "";

      const stringAtivosFormatados =
        ordem?.ativos
          ?.filter(({ ativo }) => ativo.ativado !== false)
          .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
          .map((ativo) => {
            const ativoVinculado = ativo.ativo;
            const nomeDoAtivo =
              ativoVinculado.descricao || ativoVinculado.nome || "Ativo";
            let textoAtivo = `*${nomeDoAtivo}*`;

            if (ativoVinculado?.camposPersonalizados?.length) {
              const campos = ativoVinculado.camposPersonalizados
                .map((campo) => {
                  const valorSalvo = ativo.valoresCampos?.find(
                    (v) => v.campoPersonalizadoId === campo.id,
                  );
                  return {
                    ...campo,
                    value: String(valorSalvo?.valor || ""),
                  };
                })
                .filter((campo) => campo.value)
                .map((campo) => `${campo.label}: ${campo.value}`)
                .join("\n");
              textoAtivo += `\n${campos}`;
            }

            return textoAtivo.toUpperCase();
          })
          .join("\n\n") || "NENHUM ATIVO VINCULADO.";

      const texto = await mensagemPersonalizadaService.getMensagem({
        ...ordem,
        contexto: `ordem-servico-${destCol.status.toLocaleLowerCase()}`,
        apelidoFantasia: session?.empresa?.apelidoFantasia,
        nomeCompletoCliente: nomeCliente,
        primeiroNomeCliente: getPrimeiroNome(nomeCliente),
        nomeUsuario: session?.usuario?.nome || "",
        dataFechamento: format(
          formatDate.received(ordem?.dataFechamento),
          "dd/MM/yyyy",
        ),
        dataPrevistaFechamento: format(
          formatDate.received(ordem?.dataPrevistaFechamento),
          "dd/MM/yyyy",
        ),
        dataAbertura: format(
          formatDate.received(ordem?.dataAbertura),
          "dd/MM/yyyy",
        ),
        ativos: stringAtivosFormatados,
      });

      if (!texto.isAxiosError && texto?.data) {
        modalConfirmacaoWhatsapp(cliente, texto);
      }
    } catch (error) {
      notification.erroGenericos("Falha no envio do documento");
    }
  };

  async function modalConfirmacaoWhatsapp(cliente, texto) {
    const props = {
      title: "Mudança de status realizada",
      text: `Deseja enviar uma mensagem de confirmação de status para o cliente?`,
      icon: "success",
      confirmButtonText: "CONFIRMAR",
      cancelButtonText: "FECHAR",
      showCancelButton: true,
      dangerMode: true,
    };
    const confirmacaoAlerta = await notification.confirmacaoGenericos(props);
    if (confirmacaoAlerta.isConfirmed) {
      const result = await adzapService.enviarMensagem({
        number: cliente?.celular || "",
        type: "text",
        text: texto?.data,
      });

      if (!result.isAxiosError) {
        notification.sucessoGenericos("Documento enviado com sucesso!");
        history.push("/faturamento/ordem-servicos");
      } else {
        responseErros(result);
      }
    }
  }

  const emitirAtualizacaoSocket = (ordem, novosValores) => {
    try {
      const socket = window.__SAF_SOCKET;
      if (!socket) return;

      const empresa = session?.empresa;

      socket.emit("messageToServerKanban", {
        id: ordem.id,
        status: novosValores.status,
        statusPersonalizadoId: novosValores.statusPersonalizadoId,
        dataFechamento: novosValores.dataFechamento,
        empresaId: empresa?.id ?? null,
      });
    } catch (err) {
      console.error("Erro ao emitir kanban update", err);
    }
  };

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (isCooldown || !destination) return;
    const isEqualDestination =
      source.droppableId === destination.droppableId &&
      source.index === destination.index;
    if (isEqualDestination) return;
    const ordemId = isNaN(Number(draggableId))
      ? draggableId
      : Number(draggableId);

    const ordensKanbanCombinadas = [
      ...ordensServicoKanban,
      ...ordensServicoFechadasDia,
    ];

    const ordem = ordensKanbanCombinadas.find(
      (o) => String(o.id) === String(ordemId),
    );
    if (!ordem) return;
    const destCol = colunasKanban.find(
      (c) => c.status === destination.droppableId,
    );
    if (!destCol) return;
    const novosValores = getNovosValoresStatus(destCol, ordem);

    const prevOrdensKanban = ordensServicoKanban;
    const prevOrdensFechadas = ordensServicoFechadasDia;

    const ordemAtualizada = { ...ordem, ...novosValores };

    setOrdensServicoKanban((prev) => {
      const semOrdemAtual = prev.filter((o) => o.id !== ordem.id);
      if (novosValores.status === "FECHADO") {
        return semOrdemAtual;
      }

      return [ordemAtualizada, ...semOrdemAtual].sort((a, b) => {
        const ta = a?.dataAbertura ? new Date(a.dataAbertura).getTime() : 0;
        const tb = b?.dataAbertura ? new Date(b.dataAbertura).getTime() : 0;
        return tb - ta;
      });
    });

    setOrdensServicoFechadasDia((prev) => {
      const semOrdemAtual = prev.filter((o) => o.id !== ordem.id);
      if (novosValores.status !== "FECHADO") {
        return semOrdemAtual;
      }

      return [...semOrdemAtual, ordemAtualizada].sort((a, b) => {
        const ta = a?.dataFechamento ? new Date(a.dataFechamento).getTime() : 0;
        const tb = b?.dataFechamento ? new Date(b.dataFechamento).getTime() : 0;
        return tb - ta;
      });
    });

    try {
      const response = await ordemServicoService.atualizarStatus(ordem.id, {
        statusPersonalizadoId: novosValores.statusPersonalizadoId,
        status: novosValores.status,
        dataFechamento: novosValores.dataFechamento,
      });
      if (response?.isAxiosError) {
        setOrdensServicoKanban(prevOrdensKanban);
        setOrdensServicoFechadasDia(prevOrdensFechadas);
        return responseErros(response);
      }
      notification.alteracaoSucesso();
      emitirAtualizacaoSocket(ordem, novosValores);
      if (session?.empresa.whatsappSimples) {
        const cliente =
          listaPessoas.find((p) => p.id === ordem.clienteId) || null;
        enviarMensagem(ordem, destCol, cliente);
      }
    } catch (err) {
      setOrdensServicoKanban(prevOrdensKanban);
      setOrdensServicoFechadasDia(prevOrdensFechadas);
      responseErros(err);
    } finally {
      iniciarCooldown();
    }
  };

  const ordensPorColuna = useMemo(() => {
    const ordensKanbanCombinadas = [
      ...ordensServicoKanban,
      ...ordensServicoFechadasDia,
    ];

    const agrupado = {};
    colunasKanban.forEach((coluna) => {
      const lista = ordensKanbanCombinadas.filter((ordem) =>
        pertenceAColuna(ordem, coluna),
      );

      lista.sort((a, b) => {
        if (coluna.status === "FECHADO") {
          const ta = a?.dataFechamento
            ? new Date(a.dataFechamento).getTime()
            : 0;
          const tb = b?.dataFechamento
            ? new Date(b.dataFechamento).getTime()
            : 0;
          return tb - ta;
        }

        const aa = a?.dataAbertura ? new Date(a.dataAbertura).getTime() : 0;
        const ba = b?.dataAbertura ? new Date(b.dataAbertura).getTime() : 0;
        return ba - aa;
      });

      agrupado[coluna.status] = lista;
    });
    return agrupado;
  }, [colunasKanban, ordensServicoKanban, ordensServicoFechadasDia]);

  const handleToggle = (event) => {
    setOpen((prevOpen) => !prevOpen);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (anchorEl.current && anchorEl.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <div className="d-flex flex-column">
      <Card>
        <CardHeader
          title="Ordens de Serviço"
          botaoVoltar
          action={
            <div className="d-flex gap-3 px-2">
              {isCooldown && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginLeft: 8,
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={cooldownProgress}
                    sx={{ width: 100, height: 6, borderRadius: 2 }}
                  />
                  <Typography sx={{ fontSize: 11 }}>
                    {Math.ceil((3000 * (1 - cooldownProgress / 100)) / 1000)}s
                  </Typography>
                </div>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <StyledTabs
                    value={abaValue}
                    onChange={handleChangeAba}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                  >
                    <StyledTab
                      label={<i className="ph-bold ph-list"></i>}
                      {...tabProps(0)}
                    />
                    <StyledTab
                      label={<i className="ph-fill ph-kanban"></i>}
                      {...tabProps(1)}
                    />
                  </StyledTabs>
                </Grid>
              </Grid>
              <ButtonGroup
                variant="contained"
                color="primary"
                disabled={
                  !permissoesHelper.temPermisao("ordem-servicos-cadastrar")
                }
              >
                <Button
                  onClick={() => onClickCadastrarOrdemServico()}
                  color="primary"
                  bold
                  fullWidth
                  variant="contained"
                >
                  Adicionar
                </Button>
                <Button
                  size="small"
                  aria-controls={open ? "split-button-menu" : undefined}
                  aria-expanded={open ? "true" : undefined}
                  aria-haspopup="menu"
                  onClick={handleToggle}
                >
                  <i
                    className="ph-fill ph-caret-down"
                    style={{ color: "#fff", fontSize: 15 }}
                  ></i>
                </Button>
              </ButtonGroup>
            </div>
          }
        />
        <CardContent>
          {abaValue === 0 ? (
            <>
              <DataGrid
                rows={ordensServico}
                rowCount={rowCount}
                columns={colunas}
                onClickEdit={onClickEditOrdemServico}
                onClickDelete={onClickDeleteOrdemServico}
                paginationMode={"server"}
                visualizarShow={permissoesHelper.temPermisao(
                  "ordem-servicos-visualizar",
                )}
                editShow={permissoesHelper.temPermisao("ordem-servicos-editar")}
                deleteShow={permissoesHelper.temPermisao(
                  "ordem-servicos-excluir",
                )}
                sendServer={(props) => sendServerDatagrid(props)}
                FormFiltroAvancado={FormFiltroAvancado}
                disableAcoes
                enableRowDoubleClick
                listaStatus={colunasKanban}
                ListStatusCustom={ListStatusCustom}
              />
            </>
          ) : (
            <Box
              className="w-100"
              sx={{
                overflowX: "auto",
                overflowY: "hidden",
                maxHeight: "calc(100vh - 200px)",
                pb: 1,
              }}
            >
              <DragDropContext onDragEnd={onDragEnd}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                    minWidth: "100%",
                  }}
                >
                  {colunasKanban
                    .filter((c) => c.status !== "CANCELADO")
                    .map((coluna) => {
                      const ordensDaColuna =
                        ordensPorColuna[coluna.status] || [];

                      return (
                        <Box
                          key={coluna.status}
                          sx={{ minWidth: 350, flex: 1 }}
                        >
                          <Droppable droppableId={coluna.status}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                <Box
                                  className="p-4 rounded-4 h-100"
                                  sx={{
                                    background: "var(--bs-gray-100)",
                                  }}
                                >
                                  <Box className="w-100 d-flex flex-column gap-3">
                                    <div className="d-flex gap-2 align-items-center">
                                      <Box
                                        sx={{
                                          width: 5,
                                          height: 16,
                                          borderRadius: "25%",
                                          backgroundColor: coluna.cor,
                                        }}
                                      />
                                      <Typography
                                        variant="h6"
                                        className="fw-bold"
                                        sx={{ fontSize: 12 }}
                                      >
                                        {coluna.status}
                                      </Typography>
                                      {coluna.status !== "FECHADO" && (
                                        <Typography
                                          variant="h6"
                                          className="fw-bold text-center p-0 m-0"
                                          sx={{
                                            fontSize: 12,
                                            width: 18,
                                            height: 18,
                                            borderRadius: "25%",
                                            backgroundColor: "#e0eefaff",
                                            color: "#244969ff",
                                          }}
                                        >
                                          {ordensDaColuna.length}
                                        </Typography>
                                      )}
                                    </div>

                                    <Box
                                      className="w-100 d-flex flex-column gap-2"
                                      sx={{
                                        overflowY: "auto",
                                        maxHeight: "calc(100vh - 320px)",
                                        pr: 1,
                                      }}
                                    >
                                      {ordensDaColuna.map((ordem, index) => (
                                        <Draggable
                                          key={ordem.id}
                                          draggableId={String(ordem.id)}
                                          index={index}
                                          isDragDisabled={isCooldown}
                                        >
                                          {(provided) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                            >
                                              <Box
                                                className="p-3 rounded-4"
                                                sx={{
                                                  position: "relative",
                                                  userSelect: "none",
                                                  background: `linear-gradient(90deg, ${coluna.cor} 1%, ${palette.white} 1%, ${palette.white} 99%)`,
                                                  boxShadow:
                                                    "0 1px 2px rgba(0,0,0,0.05)",
                                                  cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                  onClickEditOrdemServico(
                                                    ordem.id,
                                                  )
                                                }
                                              >
                                                <Typography
                                                  variant="h6"
                                                  className="fw-bold"
                                                  sx={{ fontSize: 12 }}
                                                >
                                                  Série: {ordem.serieDocumento}{" "}
                                                  | Nº Doc:{" "}
                                                  {ordem.numeroDocumento}
                                                </Typography>

                                                <Typography
                                                  variant="body1"
                                                  sx={{ fontSize: 11 }}
                                                >
                                                  Cliente:{" "}
                                                  {ordem.clienteNome ||
                                                    value.autoComplete(
                                                      listaPessoas,
                                                      ordem.clienteId,
                                                    )?.nomeRazaoSocial ||
                                                    "Não informado"}
                                                </Typography>

                                                <Typography
                                                  variant="body2"
                                                  className="text-muted"
                                                  sx={{ fontSize: 10 }}
                                                >
                                                  Abertura:{" "}
                                                  {format(
                                                    formatDate.received(
                                                      ordem.dataAbertura,
                                                    ),
                                                    "dd/MM/yyyy",
                                                  )}
                                                </Typography>
                                                {ordem.dataFechamento && (
                                                  <Typography
                                                    variant="body2"
                                                    className="text-muted"
                                                    sx={{ fontSize: 10 }}
                                                  >
                                                    Fechamento:{" "}
                                                    {format(
                                                      formatDate.received(
                                                        ordem.dataFechamento,
                                                      ),
                                                      "dd/MM/yyyy",
                                                    )}
                                                  </Typography>
                                                )}

                                                {(() => {
                                                  const prestadorIds =
                                                    getPrestadorIdsFromOrdem(
                                                      ordem,
                                                    );
                                                  if (!prestadorIds.length)
                                                    return null;
                                                  return (
                                                    <Box className="d-flex align-items-center gap-1 mt-2 justify-content-end">
                                                      {prestadorIds
                                                        .slice(0, 3)
                                                        .map((prestadorId) => (
                                                          <Avatar
                                                            id={prestadorId}
                                                            key={prestadorId}
                                                            userList={
                                                              prestadoresList
                                                            }
                                                          />
                                                        ))}
                                                      {prestadorIds.length >
                                                        3 && (
                                                        <Typography
                                                          sx={{
                                                            fontSize: 11,
                                                            color: "#64748b",
                                                          }}
                                                        >
                                                          +
                                                          {prestadorIds.length -
                                                            3}
                                                        </Typography>
                                                      )}
                                                    </Box>
                                                  );
                                                })()}
                                              </Box>
                                            </div>
                                          )}
                                        </Draggable>
                                      ))}
                                      {provided.placeholder}
                                    </Box>
                                  </Box>
                                </Box>
                              </div>
                            )}
                          </Droppable>
                        </Box>
                      );
                    })}
                </Box>
              </DragDropContext>
            </Box>
          )}
        </CardContent>
        <ImportarOrcamentoServicoDialog
          open={openDialogImportarOrcamentoServico}
          setOpen={setOpenDialogImportarOrcamentoServico}
          prestadoresList={prestadoresList}
        />
        <Popper
          className="menu-selecao"
          open={open}
          anchorEl={anchorEl}
          placement="bottom-end"
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "center botton"
                    ? "center top"
                    : "center bottom",
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList autoFocusItem>
                    {buttonSelectOpcoes.map((option) => (
                      <MenuItem
                        key={option.descricao}
                        disabled={!option.permission}
                        onClick={() => {
                          option.action(option.descricao);
                        }}
                      >
                        {option.descricao}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
        <Loading loading={loading} />
      </Card>
    </div>
  );
};

export default ListOrdensServico;
